import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { LocalStorageFacade } from '@application/localstorage';
import { TranslateService } from '@ngx-translate/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, of, pipe, switchMap } from 'rxjs';
import { LanguageService } from './infra/language.service';
import { tapResponse } from '@ngrx/operators';
import { configureDevExtremeLocalization } from '@shared/assets';
import { defaultLanguage, detectLanguage, mapLanguage } from '@shared/utils/language-loader';

export const languages = [
  { id: 'en-US', description: 'PROFILEMENU.LANGUAGES.ENGLISH' },
  { id: 'pt-BR', description: 'PROFILEMENU.LANGUAGES.PORTUGUESE' },
  { id: 'es-ES', description: 'PROFILEMENU.LANGUAGES.SPANISH' },
];

export interface ILanguageStore {
  language: string;
}

export const initialLanguageStore: ILanguageStore = {
  language: null,
};

export const LanguageStore = signalStore(
  { providedIn: 'root' },
  withState(initialLanguageStore),
  withComputed((state) => ({
    getCurrentLanguage: computed(() => state.language() || defaultLanguage),
  })),
  withMethods(
    (
      state,
      localStorageFacade = inject(LocalStorageFacade),
      translateService = inject(TranslateService),
      languageService = inject(LanguageService)
    ) => {
      /** Guard to prevent reentrant calls to setLanguage while translateService.use() is running */
      let isSettingLanguage = false;

      /**
       * Load of the translation file for the language applied last. It is the
       * loader's own `shareReplay`ed request, so a late subscriber still gets
       * the result — which is what lets `LANGUAGE_INITIALIZER` hold the
       * bootstrap until the translations are in memory.
       */
      let pendingLoad: Observable<unknown> | null = null;

      const verifyLanguage = (language: string): boolean => {
        return languages.some(({ id }) => id === language);
      };

      const setLanguage = (language: string): Observable<any> | null => {
        const mappedLanguage = mapLanguage(language);
        language = verifyLanguage(mappedLanguage) ? mappedLanguage : defaultLanguage;
        if (language === state.language()) return null;
        if (isSettingLanguage) return null;
        isSettingLanguage = true;
        patchState(state, { language });
        localStorageFacade.setItem('language', language);
        configureDevExtremeLocalization(language);
        languageService.setLanguageInAccountApp(language);
        isSettingLanguage = false;
        pendingLoad = translateService.use(language);
        return pendingLoad;
      };

      /**
       * Resolves once the translation file for the current language has landed.
       * Completes immediately when no language was applied yet — there is
       * nothing to wait for in that case.
       */
      const whenLanguageReady = (): Observable<unknown> => pendingLoad ?? of(null);

      return {
        setLanguage,
        verifyLanguage,
        whenLanguageReady,
        getLocalization: rxMethod<void>(
          pipe(
            switchMap(() =>
              languageService.getLocalization().pipe(
                tapResponse({
                  next: (localization) => {
                    // Unmappable answer: keep the machine language rather than
                    // dropping the user to `defaultLanguage`.
                    const mapped = mapLanguage(`${localization?.Language}-${localization?.CountryCode}`);
                    setLanguage(mapped ?? detectLanguage());
                  },
                  // A failed detection leaves the language already applied on init.
                  error: () => undefined,
                })
              )
            )
          )
        ),
      };
    }
  ),
  withHooks({
    onInit(
      state,
      localStorageFacade = inject(LocalStorageFacade),
      translateService = inject(TranslateService),
    ) {
      translateService.setDefaultLang(defaultLanguage);

      // A saved preference is the user's explicit choice — nothing may override it.
      const savedLanguage = mapLanguage(localStorageFacade.getItem('language'));
      if (savedLanguage) {
        state.setLanguage(savedLanguage);
        return;
      }

      // First boot: start from the machine language so the app is usable at once,
      // then let `/Localization/Detect` refine it. `setLanguage` re-maps and falls
      // back to `defaultLanguage` on its own, so no extra guard is needed here.
      state.setLanguage(detectLanguage())?.subscribe(() => state.getLocalization());
    },
  })
);
